import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import moment from 'moment';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, ReferenceLine,
} from 'recharts';
import axios from 'config/axios';
import Header from 'components/Header';
import InfoBox from 'components/InfoBox';
import Loader from 'components/Loader';
import Button from 'components/Button';

import './index.css';

// One student's BCA history. Scores only (+2 right, -0.5 wrong, 0 blank, -20
// to 80): BCA questions carry no categories, so there is no category breakdown.

const formatDuration = (seconds: number) => {
    const duration = moment.duration(seconds, 'seconds');
    const minutes = Math.floor(duration.asMinutes());
    const secs = duration.seconds();
    return minutes ? `${minutes}m ${secs}s` : `${secs}s`;
};

function HistoricalBCAReport() {
    const [data, setData] = useState(null);
    const [failed, setFailed] = useState(false);

    const { studentId } = useParams();
    const navigate = useNavigate();
    const printRef = useRef(null);
    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    useEffect(() => {
        axios.get(`tutors/digital/bca/historicalResult/${studentId}/`)
            .then(res => setData(res.data))
            .catch(error => {
                console.log(error);
                setFailed(true);
            });
    }, [studentId]);

    if (failed) return <div className='layout'><p className='bcaHistory__empty'>This history could not be loaded.</p></div>;
    if (!data) return <Loader />;

    const tests = data.tests;
    const latest = tests[tests.length - 1];
    const chartData = tests.map(test => ({
        name: test.name.replace(/^BCA\s*/, ''),
        date: test.testDate,
        score: test.score,
    }));

    return (
        <div className='layout'>
            <Helmet>
                <title>
                    Performance History (BCA) - Test Center Admin
                </title>
            </Helmet>
            <div className='bcaHistory__wrap'>
                <Header
                    title='Performance History'
                    prevRoutes={[
                        { name: 'All Students', path: '/students' },
                        { name: data.studentName, path: `/students/${studentId}` }
                    ]}
                />
                {tests.length === 0
                ?   <p className='bcaHistory__empty'>{data.studentName} has no completed BCA tests yet.</p>
                :   <>
                        <div className='bcaHistory__print'>
                            <Button onClick={handlePrint}>
                                Print Report
                            </Button>
                        </div>
                        <div className='bcaHistory__report' ref={printRef}>
                            <div className='bcaHistory__printHeader'>
                                {data.logo && <img className='bcaHistory__headerLogo' alt='logo' src={data.logo} />}
                                <div>
                                    <h1 className='bcaHistory__title'>BCA Performance History</h1>
                                    <h2 className='bcaHistory__studentName'>{data.studentName}</h2>
                                    <p>{data.email}</p>
                                </div>
                            </div>

                            <div className='bcaHistory__section'>
                                <div className='bcaHistory__sectionTitle'>Basic Information</div>
                                <div className='bcaHistory__boxes'>
                                    <InfoBox title='Name' content={data.studentName} />
                                    <InfoBox title='Organization' content={data.organization} />
                                    <InfoBox title='Since' content={moment(data.since).format('MMMM D YYYY')} />
                                    <InfoBox title='Tests Taken' content={tests.length} />
                                </div>
                            </div>

                            <div className='bcaHistory__section'>
                                <div className='bcaHistory__sectionTitle'>Latest Test · {latest.name}</div>
                                <div className='bcaHistory__boxes'>
                                    <InfoBox big title='Score' content={latest.score} smallContent={`/ ${data.maxScore}`} />
                                    <InfoBox big title='Correct' content={latest.correct} />
                                    <InfoBox big title='Incorrect' content={latest.wrong} />
                                    <InfoBox big title='Omitted' content={latest.blank} />
                                </div>
                            </div>

                            <div className='bcaHistory__section'>
                                <div className='bcaHistory__sectionTitle'>Achievements</div>
                                <div className='bcaHistory__boxes'>
                                    <InfoBox big title='All Tests Avg' content={data.avgScore} />
                                    <InfoBox big title='Best Score' content={data.bestScore} />
                                    <InfoBox big title='Test Duration Avg' content={formatDuration(data.avgDuration || 0)} />
                                    {/* middle, not big: a date in the big number font wraps out of the card */}
                                    <InfoBox middle title='Latest Test Date' content={moment(latest.testDate).format('MMM D, YYYY')} />
                                </div>
                                <div className='bcaHistory__box'>
                                    <div className='bcaHistory__boxTitle'>Score by Test (−20 to 80)</div>
                                    <div className='bcaHistory__graph'>
                                        <ResponsiveContainer width='100%' height='100%'>
                                            <BarChart data={chartData} margin={{ top: 24, right: 20, left: 0, bottom: 8 }}>
                                                <CartesianGrid strokeDasharray='3 4' vertical={false} stroke='#EAECF0' />
                                                <XAxis dataKey='name' tickLine={false} axisLine={{ stroke: '#EAECF0' }} fontSize={12} interval={0} />
                                                <YAxis domain={[data.minScore, data.maxScore]} tickLine={false} axisLine={false} fontSize={12} width={44} />
                                                <ReferenceLine y={0} stroke='#98A2B3' />
                                                <Tooltip cursor={{ fill: '#0575a209' }} />
                                                <Bar dataKey='score' name='Score' fill='#0575a2' radius={[3, 3, 0, 0]} maxBarSize={64}>
                                                    <LabelList dataKey='score' position='top' fontSize={12} fontWeight={700} fill='#313131' />
                                                </Bar>
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>

                            <div className='bcaHistory__section'>
                                <div className='bcaHistory__sectionTitle'>All Tests</div>
                                <div className='bcaHistory__box'>
                                    <table className='bcaHistory__table'>
                                        <thead>
                                            <tr>
                                                <th>Test</th>
                                                <th>Date</th>
                                                <th>Score</th>
                                                <th>Correct</th>
                                                <th>Incorrect</th>
                                                <th>Omitted</th>
                                                <th>Time</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tests.slice().reverse().map(test => (
                                                <tr key={test.testId}>
                                                    <td>
                                                        <span className='bcaHistory__link' onClick={() => navigate(`/report/digital-bca/${test.testId}`)}>
                                                            {test.name}
                                                        </span>
                                                    </td>
                                                    <td>{moment(test.testDate).format('MMM D YYYY')}</td>
                                                    <td>{test.score}</td>
                                                    <td>{test.correct}</td>
                                                    <td>{test.wrong}</td>
                                                    <td>{test.blank}</td>
                                                    <td>{formatDuration(test.duration)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
        </div>
    );
}

export default HistoricalBCAReport;
